/**
 * This source code is quoted from rc-steps.
 * homepage: https://github.com/react-component/steps
 */
import classNames from 'classnames';
import React, { Children, cloneElement, MouseEvent, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { setComponentSize } from "../../wui-core/src/componentStyle";
import { getNid, prefix, WebUI } from "../../wui-core/src/index";
import Dropdown from '../../wui-dropdown/src';
import Icon from "../../wui-icon/src";
import { getLangInfo } from '../../wui-locale/src/tool';
import Menu from '../../wui-menu/src';
import { WithConfigConsumer } from "../../wui-provider/src/context";
import Step from './Step';
import i18n from './i18n';
import { ChangePosition, StepsProps } from './iSteps';

const {Item} = Menu

const defaultProps = {
    iconPrefix: 'u',
    direction: 'horizontal',
    labelPlacement: 'horizontal',
    current: 0,
    initial: 0,
    status: 'process',
    size: 'default',
    progressDot: false,
    className: "",
    style: {},
    icons: undefined,
    more: false,
    type: 'default',
    locale: 'zh-cn',
    fieldid: undefined,
    items: []
};
const typeMap = {
    arrow: 'arrow',
    number: 'number',
    dot: 'dot',
    default: undefined
}

const Steps: React.FC<StepsProps> = (props) => {
    const {
        clsPrefix, style = {}, className, children, direction,
        labelPlacement, iconPrefix, status, size, current, progressDot, initial,
        icons, more, onChange, type, locale, items, percent, dir,
        ...restProps
    } = props;

    const [menuFrontArr, setMenuFrontArr] = useState<ReactNode[]>([]);
    const [menuAfterArr, setMenuAfterArr] = useState<ReactNode[]>([]);
    const [activeNum, setActiveNum] = useState<number>(current || -1);

    // 合并相关的 ref 到单个对象中，减少 ref 数量
    const dimensionsRef = useRef({
        frontWidth: 0,
        afterWidth: 0,
        clientWidth: 0,
        itemAllWidth: 0,
        currentFrontWidth: 0,
        currentAfterWidth: 0,
        itemLen: 0,
        transformNum: 0,
        frontNum: 0,
        afterNum: 0,
        itemClientWidth: 0,
        observerWidth: -1
    });

    const dataRef = useRef({
        itemWidthArr: [] as number[],
        stepDomArr: [] as HTMLElement[],
        observerObject: null as any
    });

    const domRef = useRef({
        stepWrapper: null as HTMLDivElement | null,
        box: null as HTMLDivElement | null
    });

    // componentDidUpdate 替换为 useEffect
    useEffect(() => {
        more && calcOverWith();
    }, [more, React.Children.count(children), items?.length]);

    // 同步current变化到activeNum
    // useEffect(() => {
    //     setActiveNum(current || -1);
    // }, [current]);

    const updateDomData = useCallback(() => {
        if (more && direction !== "vertical") {
            const rootDom = domRef.current.box as HTMLElement;
            const stepDom = domRef.current.stepWrapper as HTMLElement;
            dimensionsRef.current.clientWidth = rootDom.clientWidth;
            dataRef.current.stepDomArr = Array.prototype.slice.call(stepDom.children);
            dataRef.current.itemWidthArr = [];
            dimensionsRef.current.itemAllWidth = 0;
            dimensionsRef.current.currentFrontWidth = 0;
            dimensionsRef.current.currentAfterWidth = 0;
            dimensionsRef.current.itemLen = dataRef.current.stepDomArr.length;
            for (let i = 0; i < dimensionsRef.current.itemLen; i++) {
                let itemWidth = dataRef.current.stepDomArr[i].clientWidth;
                let itemWidthTemp = itemWidth;
                if (type === 'arrow') {
                    const marginRight = setComponentSize(size) === 'sm' ? 18 : 24;
                    itemWidthTemp += marginRight;
                }
                (i === dimensionsRef.current.itemLen - 1) && (itemWidthTemp = itemWidth);
                dataRef.current.itemWidthArr.push(itemWidthTemp);
                dimensionsRef.current.itemAllWidth += itemWidthTemp;
                if (i <= current!) {
                    dimensionsRef.current.currentFrontWidth += itemWidthTemp;
                }
                if (i >= current!) {
                    dimensionsRef.current.currentAfterWidth += itemWidthTemp;
                }
            }
        }
    }, [more, direction, current, type, size]);

    const isOverWidth = useCallback(() => {
        if (dimensionsRef.current.clientWidth >= dimensionsRef.current.itemAllWidth) {
            resetState();
            return false;
        }
        return true;
    }, []);

    const resetState = useCallback(() => {
        dimensionsRef.current.frontNum = 0;
        dimensionsRef.current.afterNum = dimensionsRef.current.itemLen;
        dimensionsRef.current.transformNum = 0;
        setMenuAfterArr([]);
        setMenuFrontArr([]);
    }, []);

    const calcOverWith = useCallback(() => {
        if (more && direction !== "vertical") {
            updateDomData();
            const clientWidth = dimensionsRef.current.clientWidth;
            const itemAllWidth = dimensionsRef.current.itemAllWidth;
            const currentFrontWidth = dimensionsRef.current.currentFrontWidth;
            const currentAfterWidth = dimensionsRef.current.currentAfterWidth;
            const itemLen = dimensionsRef.current.itemLen;

            if (clientWidth < itemAllWidth) {
                if (currentFrontWidth <= clientWidth) {
                    dimensionsRef.current.frontNum = 0;
                    setMenuArr('left');
                } else if (currentAfterWidth <= clientWidth) {
                    dimensionsRef.current.afterNum = itemLen;
                    setMenuArr('right');
                } else {
                    dimensionsRef.current.frontNum = current!;
                    setMenuArr('left');
                }
            } else {
                resetState();
            }
        }
    }, [more, direction, current, updateDomData, resetState]);

    useEffect(() => {
        // 断开旧的
        if (dataRef.current.observerObject) {
            dataRef.current.observerObject.disconnect();
        }

        if (!more) return;

        // 创建新的
        const stepParentDom = domRef.current.stepWrapper?.parentElement;
        if (!stepParentDom) return;

        dataRef.current.observerObject = new ResizeObserver((entries: any[]) => {
            entries.forEach((item) => {
                const newWidth = item.contentRect.width;
                if (dimensionsRef.current.observerWidth === -1) {
                    dimensionsRef.current.observerWidth = newWidth;
                }
                if (dimensionsRef.current.observerWidth !== newWidth) {
                    dimensionsRef.current.observerWidth = newWidth;
                    newWidth !== 0 && calcOverWith(); // 直接使用当前作用域的 calcOverWith
                }
            });
        });

        dataRef.current.observerObject.observe(stepParentDom);

        return () => {
            dataRef.current.observerObject?.disconnect();
        };
    }, [calcOverWith, more]); // 依赖 calcOverWith

    const setMenuArr = useCallback((d: string) => {
        if (!isOverWidth()) return;
        let itemWidthArr = dataRef.current.itemWidthArr;
        let clientWidth = dimensionsRef.current.clientWidth;
        let stepDomArr = dataRef.current.stepDomArr;
        let itemLen = dimensionsRef.current.itemLen;
        let num = 0;

        dimensionsRef.current.frontWidth = setComponentSize(size) === 'sm' ? 62 : 54;
        dimensionsRef.current.afterWidth = setComponentSize(size) === 'sm' ? 22 : 14;

        if (type === 'arrow') {
            const marginRight = setComponentSize(size) === 'sm' ? 18 : 24;
            dimensionsRef.current.frontWidth = 160 + marginRight;
            dimensionsRef.current.afterWidth = 160;
            dimensionsRef.current.afterNum = dimensionsRef.current.afterNum === itemLen - 1 ? itemLen : dimensionsRef.current.afterNum;
            dimensionsRef.current.frontNum = dimensionsRef.current.frontNum === 1 ? 0 : dimensionsRef.current.frontNum;
        }
        if (type === 'dot') {
            dimensionsRef.current.frontWidth = 80;
            dimensionsRef.current.afterWidth = 85;
        }
        if (type === 'number') {
            dimensionsRef.current.frontWidth = 80;
            dimensionsRef.current.afterWidth = 55;
        }
        if (d === 'right') {
            if (dimensionsRef.current.afterNum === itemLen) {
                clientWidth = clientWidth - dimensionsRef.current.frontWidth;
            } else {
                clientWidth = clientWidth - dimensionsRef.current.frontWidth - dimensionsRef.current.afterWidth;
            }
            for (let i = dimensionsRef.current.afterNum - 1; i >= 0; i--) {
                num += itemWidthArr[i];
                if (num >= clientWidth) {
                    dimensionsRef.current.frontNum = i + 1;
                    break;
                }
            }
        } else {
            if (dimensionsRef.current.frontNum === 0) {
                clientWidth = clientWidth - dimensionsRef.current.afterWidth;
            } else {
                clientWidth = clientWidth - dimensionsRef.current.frontWidth - dimensionsRef.current.afterWidth;
            }
            for (let i = dimensionsRef.current.frontNum; i < itemLen; i++) {
                num += itemWidthArr[i];
                if (num > clientWidth) {
                    dimensionsRef.current.afterNum = i;
                    break;
                }
            }
        }


        // 设置transform的偏移量和 右 更多按钮偏移量 type === 'arrow' 时， 无需处理该逻辑
        dimensionsRef.current.transformNum = 0;
        dimensionsRef.current.itemClientWidth = 0;
        if (type !== 'arrow') {
            dimensionsRef.current.itemClientWidth = itemWidthArr.slice(dimensionsRef.current.frontNum, dimensionsRef.current.afterNum).reduce((t, v) => {
                return t + v;
            }, 0);
            if (dimensionsRef.current.frontNum !== 0) {
                dimensionsRef.current.transformNum = itemWidthArr.slice(0, dimensionsRef.current.frontNum).reduce((t, v) => {
                    return t + v;
                }, 0) - dimensionsRef.current.frontWidth;
                dimensionsRef.current.itemClientWidth += dimensionsRef.current.frontWidth;
            }
        }

        const isValidNode = (node: HTMLElement) => {
            if (node && node.dataset.text) {
                return node.dataset.text;
            }
            const titleNode = node.querySelector(`.${prefix}-steps-item-title`);
            return titleNode?.textContent;
        };

        let dropDownAfterArr = stepDomArr.slice(dimensionsRef.current.afterNum);
        let dropDownFrontArr = stepDomArr.slice(0, dimensionsRef.current.frontNum);
        let menuAfterArrItems = (
            dropDownAfterArr.map((item) =>
                item && <Item key={item.dataset.num} data-status={item.dataset.status}>{isValidNode(item)}</Item>
            )
        );
        let menuFrontArrItems = (
            dropDownFrontArr.map((item) =>
                item && <Item key={item.dataset.num} data-status={item.dataset.status}>{isValidNode(item)}</Item>
            )
        );
        setMenuAfterArr(menuAfterArrItems);
        setMenuFrontArr(menuFrontArrItems);
    }, [size, type, isOverWidth]);

    const dropDownChange = useCallback((val: { key: string, domEvent: MouseEvent}, position: ChangePosition) => {
        const {key, domEvent} = val;
        if (position === 'left') {
            dimensionsRef.current.frontNum = parseInt(key);
            dimensionsRef.current.afterNum = dimensionsRef.current.afterNum - 1;
        } else {
            dimensionsRef.current.frontNum = dimensionsRef.current.frontNum + 1;
            dimensionsRef.current.afterNum = parseInt(key) + 1;
        }
        updateDomData();
        setMenuArr(position);
        if (onChange && (domEvent.target as HTMLLIElement).dataset.status !== 'true') {
            onStepClick(parseInt(key), position);
        }
    }, [onChange, updateDomData, setMenuArr]);

    const prev = useCallback((val: { key: string, domEvent: MouseEvent}) => {
        dropDownChange(val, 'left');
    }, [dropDownChange]);

    const next = useCallback((val: { key: string, domEvent: MouseEvent}) => {
        dropDownChange(val, 'right');
    }, [dropDownChange]);

    const onStepClick = useCallback((next: number, position: ChangePosition = 'center') => {
        setActiveNum(next);
        if (onChange && current !== next) {
            onChange(next, position);
        }
    }, [onChange, current]);

    // 性能优化1：缓存 filteredChildren 计算，避免每次渲染都执行 map 和 filter
    const filteredChildren = React.useMemo(() => {
        const itemsChildren = items?.map((items, i) => <Step {...items} key={i} />);
        return itemsChildren!.length !== 0
            ? React.Children.toArray(itemsChildren).filter(c => !!c)
            : React.Children.toArray(children).filter(c => !!c);
    }, [items, children]);

    // 性能优化2：缓存常量和 size 计算，避免 size 变化时重复调用 setComponentSize
    const { typeTemp, adjustedlabelPlacement, computedSize } = React.useMemo(() => {
        const isDot = !!progressDot || type === 'dot';
        const sizeValue = setComponentSize(size);
        return {
            typeTemp: isDot ? 'dot' : type,
            adjustedlabelPlacement: isDot ? 'vertical' : labelPlacement,
            computedSize: sizeValue
        };
    }, [progressDot, type, labelPlacement, size]);

    // 性能优化3：缓存 className 计算
    const classString = React.useMemo(() => {
        return classNames(clsPrefix, `${clsPrefix}-${direction}`, `${clsPrefix}-${direction}-${dir}`, className, {
            [`${clsPrefix}-${computedSize == 'sm' ? 'small' : ''}`]: computedSize as any == 'default' ? false : computedSize,
            [`${clsPrefix}-label-${adjustedlabelPlacement}`]: direction === 'horizontal',
            [`${clsPrefix}-more`]: more,
            [`${clsPrefix}-${typeTemp}`]: typeTemp && typeMap[typeTemp as keyof typeof typeMap]
        });
    }, [clsPrefix, direction, dir, className, computedSize, adjustedlabelPlacement, more, typeTemp]);

    // 性能优化4：缓存 localeText，减少国际化查找开销
    const localeText = React.useMemo(() => {
        return getLangInfo(locale!, i18n, 'steps').langMap.text;
    }, [locale]);

    // 暂不缓存 会出bug
    const stepChild = Children.map(filteredChildren, (child: React.ReactElement, index: number) => {
        if (!child) {
            return null;
        }

        let out = false;
        if (more && (dimensionsRef.current.afterNum <= index || dimensionsRef.current.frontNum > index)) {
            out = true;
        }

        const stepNumber = initial! + index;
        const handleStepClick = onChange ? onStepClick : undefined;
        const childProps = {
            stepNumber: `${stepNumber + 1}`,
            stepIndex: index,
            active: activeNum === stepNumber,
            clsPrefix,
            iconPrefix,
            wrapperStyle: style,
            progressDot,
            icons,
            more,
            out,
            onStepClick: handleStepClick,
            type,
            stepFieldId: props.fieldid,
            percent,
            size,
            direction,
            ...child.props,
        };
        if (status === 'error' && index === current! - 1) {
            childProps.className = `${clsPrefix}-next-error`;
        }
        if (!child.props.status) {
            if (stepNumber === current) {
                childProps.status = status;
            } else if (stepNumber < current!) {
                childProps.status = 'finish';
            } else {
                childProps.status = 'wait';
            }
        }
        return cloneElement(child, childProps);
    });
    // 过滤掉隐藏的step
    const visibleChild = stepChild.filter(c => c.props.out === false);
    let adapterNid = getNid(props);

    // 性能优化6&7：缓存 Menu 和 Dropdown 组件
    // 重要：useMemo 必须在条件语句外调用，符合 React Hooks 规则
    const frontMenu = React.useMemo(() => (
        <Menu onClick={prev}>
            {menuFrontArr}
        </Menu>
    ), [menuFrontArr, prev]);

    const afterMenu = React.useMemo(() => (
        <Menu onClick={next}>
            {menuAfterArr}
        </Menu>
    ), [menuAfterArr, next]);

    const frontDropdown = React.useMemo(() => (
        <div className={`${clsPrefix}-more-front-dropdown`}>
            <Dropdown
                trigger={['hover']}
                overlay={frontMenu}
                animation="slide-up"
            >
                <span className={`${clsPrefix}-tab-more-select`}>
                    <Icon type="uf-gengduo"/>
                    {(type === 'arrow') && (<><span className={`${clsPrefix}-tab-more-select-title`}>{localeText}</span><Icon type="uf-correct-2"/></>)}
                </span>
            </Dropdown>
            <div className={`${clsPrefix}-more-front-line`}></div>
        </div>
    ), [clsPrefix, frontMenu, type, localeText]);

    const afterDropdown = React.useMemo(() => (
        <div className={`${clsPrefix}-more-after-dropdown`} style={{[(dir === 'rtl' && type === 'arrow') || (dir !== 'rtl' && type !== 'arrow') ? 'left' : 'right']: dimensionsRef.current.itemClientWidth}}>
            <Dropdown
                trigger={['hover']}
                overlay={afterMenu}
                animation="slide-up"
            >
                <span className={`${clsPrefix}-tab-more-select`}>
                    {(type !== 'arrow') && (<Icon type="uf-gengduo"/>)}
                    {(type === 'arrow') && (<div className={`${clsPrefix}-tab-more-select-section`}><Icon type="uf-gengduo"/><span className={`${clsPrefix}-tab-more-select-title`}>{localeText}</span></div>)}
                </span>
            </Dropdown>
        </div>
    ), [clsPrefix, afterMenu, dir, type, localeText]);

    if (more && direction !== 'vertical') {

        let stepWrapper = (
            <div className={`${clsPrefix}-step-wrapper`} ref={(el) => domRef.current.stepWrapper = el}
                style={{transform: `translate3d(${dir === 'rtl' ? dimensionsRef.current.transformNum : -dimensionsRef.current.transformNum}px,0,0)`}}>
                {stepChild}
            </div>
        );

        // 箭头模式下，显示的step在另一个可视容器中
        if (type === 'arrow') {
            stepWrapper = (
                <>
                    {/* 显示的step容器 */}
                    <div className={`${clsPrefix}-step-wrapper ${clsPrefix}-step-visible-wrapper`}
                        style={{
                            padding: `0 ${menuAfterArr.length !== 0 ? dimensionsRef.current.afterWidth + (setComponentSize(size) === 'sm' ? 18 : 24) : 0}px 0 ${menuFrontArr.length !== 0 ? dimensionsRef.current.frontWidth : 0}px`,
                        }}
                    >
                        {visibleChild}
                    </div>
                    {/* 所有step容器 */}
                    <div className={`${clsPrefix}-step-wrapper ${clsPrefix}-step-hidden-wrapper`} ref={(el) => domRef.current.stepWrapper = el}>
                        {stepChild}
                    </div>
                </>
            );
        }

        return (
            <div className={classString} style={style} {...restProps} {...adapterNid} ref={(el) => domRef.current.box = el}>
                {menuFrontArr.length !== 0 && frontDropdown}
                {menuAfterArr.length !== 0 && afterDropdown}
                {stepWrapper}
            </div>
        );
    }

    return (
        <div className={classString} style={style} {...restProps} {...adapterNid} ref={(el) => domRef.current.box = el}>
            {stepChild}
        </div>
    );
};

// 添加静态属性和 displayName
Steps.displayName = 'Steps';
(Steps as any).Step = Step;

// 装饰器导出
export default WithConfigConsumer({name: 'steps'})(WebUI({name: "steps", defaultProps})(Steps));
